import dayjs from "dayjs"
import {
  applyPriceAdjustments,
  getApplicableDayAdjustments,
  getApplicableSeasonalAdjustments,
  getProgressiveAdjustment,
  PROGRESSIVE_DIRECTION,
  PROGRESSIVE_UNIT
} from "./calculateBookingTotal"
import { enumerateDates, countDays } from "./dateSpan"

// Re-exported so existing callers keep one import for span + pricing helpers. getProgressiveAdjustment
// itself now lives in calculateBookingTotal.js (calculateBookingTotal's own per-day pricing needs it
// too, and this module already depends on calculateBookingTotal for the adjustment primitives below,
// so defining it there avoids a cycle) — re-exported here so nothing that already imports it from this
// module has to change.
export { enumerateDates, countDays, getProgressiveAdjustment, PROGRESSIVE_DIRECTION, PROGRESSIVE_UNIT }

export const DURATION_TYPES = { SINGLE_DAY: "single_day", MULTI_DAY: "multi_day" }
export const DURATION_MODES = { FIXED: "fixed", DYNAMIC: "dynamic" }

export const isMultiDayTrip = (trip) => trip?.duration_type === DURATION_TYPES.MULTI_DAY

// Fixed sells named durations (each a trip_price); dynamic sells a daily rate over a guest-chosen range.
export const isDynamicMultiDay = (trip) => isMultiDayTrip(trip) && trip?.duration_mode === DURATION_MODES.DYNAMIC
export const isFixedMultiDay = (trip) => isMultiDayTrip(trip) && trip?.duration_mode !== DURATION_MODES.DYNAMIC

// A dynamic multi-day trip's registered `date` Form.Item only carries the start date — the guide
// must also pick an end date (held in bookingSelections, with no Form.Item of its own) before the
// booking form can advance past step 1. Shared by the create and edit Continue/step gates so the
// condition can't drift between the two flows.
export const isDynamicRangeIncomplete = (trip, bookingSelections) => isDynamicMultiDay(trip) && !bookingSelections?.endDate

// Whether the calendar still needs guide input before the booking form can advance: either
// nothing has been picked yet at all (any experience type), or — for a dynamic multi-day trip
// specifically — a start was picked with no end. Both cases must show a VISIBLE error: the
// calendar's own `date` Form.Item is deliberately `hidden` (see ExperienceDetail.jsx, which uses
// the custom DateSelector as the real UI instead), so antd's own required-field validation can
// reject silently with nothing shown on screen unless this is checked explicitly and surfaced
// through DateSelector's own error prop.
export const isDateSelectionIncomplete = (trip, bookingSelections) =>
  !bookingSelections?.date || isDynamicRangeIncomplete(trip, bookingSelections)

const toIsoDate = (value) => (value ? dayjs(value).format("YYYY-MM-DD") : "")


/** Day + seasonal adjustments that apply to one date, in the shape applyPriceAdjustments expects. */
export const getAdjustmentsForDate = (trip, isoDate) => {
  if (!isoDate) return []
  return [
    ...getApplicableDayAdjustments(trip?.day_adjustments, isoDate),
    ...getApplicableSeasonalAdjustments(trip?.seasonal_adjustments, isoDate, trip?.available_date_ranges)
  ]
}

/**
 * Prices one unit rate (a per-person rate or the flat daily rate) across a multi-day span (fixed
 * per-day, or dynamic).
 *
 * Day/seasonal adjustments are re-evaluated per individual calendar day — each covered date gets
 * its own weekday/season match, summed across the span. NOTE: this is a deliberate product
 * choice, not what the backend actually charges — TripBookings::PriceAdjustmentCalculator is only
 * ever constructed with the booking's single booked_date, so on a range that crosses a
 * weekend/weekday or season boundary, this preview WILL diverge from the real charge. Chosen
 * anyway because per-day variation reads more intuitively on the calendar.
 *
 * Progressive duration pricing (dynamic only) is the one genuinely per-day-index component: each
 * day's own ramp step, summed separately and added to the same base. Only the PERCENT-unit ramp is
 * embedded here — it's scale-invariant, so it's still correct once this per-unit rate is later
 * multiplied by its own guest type's count. A FIXED-unit ($) ramp is one flat delta per ramped day
 * for the WHOLE per-day charge (every guest combined), not a per-guest amount — embedding it here
 * would have it multiplied AGAIN by that count downstream, and a second time for every other guest
 * type with a nonzero count too. Callers that need the fixed-unit ramp (dynamic trips with
 * progressive_pricing_unit "fixed") must add it once at the total level themselves, off the
 * aggregate per-day rate across all guest types — see calculateBookingTotal.js's
 * progressiveFixedDeltaTotal for the reference implementation and the exact backend citation.
 *
 * @param {Number|String} baseRate - the guide's daily rate for one guest type
 * @param {Object} trip - experience record (adjustments + progressive pricing config)
 * @param {String[]} dates - ISO dates covered by the booking, in order
 */
export const priceRateAcrossDates = (baseRate, trip, dates) => {
  const base = Number.parseFloat(baseRate ?? 0) || 0
  if (!base || !dates?.length) return { total: 0, baseTotal: 0 }

  const isDynamic = isDynamicMultiDay(trip)

  return dates.reduce(
    (acc, isoDate, index) => {
      const daySeasonalDelta = applyPriceAdjustments(base, getAdjustmentsForDate(trip, isoDate)) - base
      let progressiveDelta = 0
      if (isDynamic) {
        const { multiplier } = getProgressiveAdjustment(trip, index + 1)
        progressiveDelta = Math.max(base * (multiplier - 1), -base)
      }
      return {
        total: acc.total + Math.max(0, base + daySeasonalDelta + progressiveDelta),
        baseTotal: acc.baseTotal + base
      }
    },
    { total: 0, baseTotal: 0 }
  )
}

/**
 * Prices one unit rate for a whole booking, whichever shape the experience sells.
 *
 * A per-day experience (dynamic multi-day) charges the rate once per covered date, each date
 * carrying its own adjustments and progressive step. Everything else is a single trip price
 * adjusted by the start date's adjustments.
 *
 * @param {Number|String} rate - the guide's rate for one guest type
 * @param {Object} trip - experience record (adjustments + progressive pricing config)
 * @param {String[]} dates - ISO dates the booking covers, in order; dates[0] is the start date
 * @param {Boolean} isPerDay - true when the rate is a daily rate rather than a whole-trip price
 * @returns {{ total: Number, baseTotal: Number }} adjusted price and its unadjusted baseline
 */
export const priceUnitForBooking = (rate, trip, dates, isPerDay) => {
  if (isPerDay && dates?.length) return priceRateAcrossDates(rate, trip, dates)
  const base = Number.parseFloat(rate ?? 0) || 0
  return { total: applyPriceAdjustments(base, getAdjustmentsForDate(trip, dates?.[0])), baseTotal: base }
}

/**
 * Booking options a guest can pick from on a fixed multi-day experience, newest-first by duration.
 * `fromPrice` is the cheapest adult-facing rate, matching the design's "From $700 / adult".
 */
export const getFixedDurationOptions = (trip) =>
  // Guarded on multi-day: on a single-day trip `trip_duration` is a length in MINUTES, and reading
  // it as a day count would invent booking options out of every price row.
  (isMultiDayTrip(trip) ? trip?.trip_prices || [] : [])
    .map((price) => ({ ...price, duration_days: price?.duration_days ?? price?.trip_duration }))
    .filter((price) => price?.duration_days != null)
    .map((price) => ({
      key: price.id,
      days: Number(price.duration_days),
      isFlatRate: price.pricing_type === "charge_flat_rate",
      fromPrice: Number(price.pricing_type === "charge_flat_rate" ? price.flat_rate : price.price_per_person) || 0,
      price
    }))
    .sort((a, b) => a.days - b.days)

/**
 * "2-10 days" for dynamic, "2, 3, 4 days" for fixed — the duration line in the About section.
 * Falls back to duration_value for legacy multi-day records that predate the two modes.
 */
export const formatMultiDayTerms = (trip) => {
  if (!isMultiDayTrip(trip)) return null

  if (isDynamicMultiDay(trip)) {
    const min = Number(trip.min_duration_days) || 0
    const max = Number(trip.max_duration_days) || 0
    if (min && max) return `${min}-${max} days`
    if (min) return `${min}+ days`
  } else {
    const days = getFixedDurationOptions(trip).map((option) => option.days)
    if (days.length === 1) return `${days[0]} days`
    if (days.length > 1) return `${days.join(", ")} days`
  }

  const legacyDays = Number(trip.duration_value) || 0
  return legacyDays ? `${legacyDays} days` : null
}

export { toIsoDate }
